import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { RiChatNewLine } from 'react-icons/ri';
import { getChattedUserListAPI } from '../../utils/api';
import InlineLoader from '../../components/loader/InlineLoader';
import './UserList.css';
import { useDispatch } from 'react-redux';
import { setChatUser } from '../../store/slices/chatState';

const UserList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchChattedUsers();
    }, []);

    useEffect(() => {
        if (searchQuery.trim() === '') {
            setFilteredUsers(users);
        } else {
            const filtered = users.filter(user =>
                user?.name?.toLowerCase()?.includes(searchQuery.toLowerCase())
            );
            setFilteredUsers(filtered);
        }
    }, [searchQuery, users]);

    const fetchChattedUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getChattedUserListAPI();

            if (response?.status === 200) {
                const userData = response?.data || [];
                setUsers(userData);
                setFilteredUsers(userData);
            } else {
                setError(typeof response?.message === 'string' ? response?.message : 'Failed to fetch users');
            }
        } catch (error) {
            console.error('Error fetching chatted users:', error);
            setError('An error occurred while fetching users');
        } finally {
            setLoading(false);
        }
    };

    const handleUserClick = (user) => {
        dispatch(setChatUser(user));
        navigate('/chat-conversation', {
            state: {
                userData: user
            }
        });
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';

        const messageDate = moment(dateString);
        const today = moment();
        const yesterday = moment().subtract(1, 'day');

        if (messageDate.isSame(today, 'day')) {
            return 'Today';
        } else if (messageDate.isSame(yesterday, 'day')) {
            return 'Yesterday';
        } else {
            return messageDate.format('M/D/YY');
        }
    };

    const getLastMessageText = (lastMessage) => {
        if (!lastMessage) return 'No messages yet';

        if (lastMessage.image) {
            return '📷 Image';
        }

        return lastMessage.text || 'No messages yet';
    };

    const getUserInitial = (name) => {
        if (!name) return '?';
        return name.charAt(0).toUpperCase();
    };

    const handleAddUser = () => {
        // Navigate to all users list
        navigate('/all-users');
    };

    if (loading) {
        return (
            <div className="user-list-container">
                <div className="user-list-header">
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search users"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        aria-label="Search users"
                    />

                    <button className="add-user-btn" onClick={handleAddUser}>
                        <RiChatNewLine size={20} />
                    </button>
                </div>
                <InlineLoader text="Loading users..." />
            </div>
        );
    }

    if (error) {
        return (
            <div className="user-list-container">
                <div className="user-list-header">
                    <input
                        type="text"
                        className="search-input"
                        placeholder="Search users"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        aria-label="Search users"
                    />

                    <button className="add-user-btn" onClick={handleAddUser}>
                        <RiChatNewLine size={20} />
                    </button>
                </div>
                <div className="error-message">
                    <p>{error}</p>
                    <button onClick={fetchChattedUsers} className="retry-btn">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="user-list-container">
            <div className="user-list-header">
                <input
                    type="text"
                    placeholder="Search users"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="search-input"
                    aria-label="Search users"
                />
                <button className="add-user-btn" onClick={handleAddUser}>
                    <RiChatNewLine size={20} />
                </button>
            </div>

            <div className="user-list-content">
                {filteredUsers.length === 0 ? (
                    <div className="user-list-empty-state">
                        <p>{searchQuery ? 'No users found matching your search' : 'No chat conversations yet'}</p>
                    </div>
                ) : (
                    <div className={`user-cards-list`}>
                        {filteredUsers.map((user) => (
                            <div
                                key={user?._id}
                                className="user-card"
                                onClick={() => handleUserClick(user)}
                            >
                                <div className="user-avatar">
                                    <span className="avatar-text">{getUserInitial(user?.name)}</span>
                                </div>

                                <div className="user-info">
                                    <div className="user-name">{user?.name}</div>
                                    <div className="last-message">
                                        {getLastMessageText(user.last_message)}
                                    </div>
                                </div>

                                <div className="user-meta">
                                    <div className="message-date">
                                        {formatDate(user?.last_message?.createdAt)}
                                    </div>
                                    {user?.unread_count > 0 && (
                                        <div className="unread-badge">
                                            {user?.unread_count}
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default UserList;
